import { useContext, useEffect, useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  Grid,
  MenuItem,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import axios from "axios";

import { AuthContext } from "../../../contexts/AuthContext";
import { CONFIG } from "../../../services/config";

import { FormInputText } from "../../../components/form/FormInputText";
import ActionButtons from "../../../components/MyModal/ActionButtons";
import MyModal from "../../../components/MyModal";

type Props = {
  onClose: Function;
  openSucessAlert?: Function;
  openErrorAlert?: Function;
};

enum ParticipantReportType {
  ALL = "ALL",
  EMAIL = "EMAIL",
  BATCH = "BATCH",
  VEHICLE = "VEHICLE",
}

enum CampaignStatus {
  ALL = "ALL",
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
  CANCELLED = "CANCELLED",
}

export interface FormValues {
  campaignStatus: CampaignStatus | "";
  campaignType: ParticipantReportType | "";
  startDate: string;
  endDate: string;
}

interface ReportJob {
  jobId: string;
  downloadUrl: string;
  status: "pending" | "processing" | "done" | "failed";
}

export default function ModalOttoReport({
  onClose,
  openSucessAlert,
  openErrorAlert,
}: Props) {
  const { t } = useTranslation();
  const {
    tokenHeaders,
    country: { countryCode },
  } = useContext(AuthContext);
  const reportPollingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const [reportJob, setReportJob] = useState<ReportJob>({
    jobId: "",
    downloadUrl: "",
    status: "pending",
  });

  const formData = yup.object().shape({
    campaignStatus: yup
      .mixed<CampaignStatus>()
      .oneOf(Object.values(CampaignStatus))
      .required(),
    campaignType: yup
      .mixed<ParticipantReportType>()
      .oneOf(Object.values(ParticipantReportType))
      .required(),
    startDate: yup
      .string()
      .required(t("ottoTrialPeriod.add.validation.startDateRequired")),
    endDate: yup
      .string()
      .required(t("ottoTrialPeriod.add.validation.endDateRequired"))
      .test(
        "is-valid-range",
        t("ottoTrialPeriod.add.validation.endDateGreaterThanStartDate"),
        (value, context) => {
          const { startDate } = context.parent as FormValues;
          if (!startDate || !value) {
            return true;
          }

          return value > startDate;
        },
      ),
  });

  const { register, handleSubmit, formState, watch } = useForm<FormValues>({
    resolver: yupResolver(formData),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      campaignStatus: "",
      campaignType: "",
      startDate: "",
      endDate: "",
    },
  });

  const { errors } = formState;
  const selectedCampaignStatus = watch("campaignStatus");
  const selectedCampaignType = watch("campaignType");

  const verifyReportStatus = async (jobId: string) => {
    const response = await axios.get(
      `${CONFIG.API_URL}temporaryAccess/report/${jobId}`,
      {
        headers: {
          ...tokenHeaders.headers,
          countryCode,
        },
      },
    );

    return response.data;
  };

  const triggerDownload = (downloadUrl: string) => {
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.click();
  };

  const startReportStatusLoopCheck = async (jobId: string) => {
    const result = await verifyReportStatus(jobId);

    if (result.status === "PENDING" || result.status === "PROCESSING") {
      reportPollingTimeoutRef.current = setTimeout(() => {
        setReportJob((old) => ({ ...old, jobId, status: "processing" }));
      }, 3000);

      return;
    }

    if (result.status === "DONE") {
      setReportJob({
        jobId,
        downloadUrl: result.downloadUrl || "",
        status: "done",
      });
      return;
    }

    setReportJob({ jobId, downloadUrl: "", status: "failed" });
  };

  useEffect(() => {
    if (reportJob.status === "processing") {
      startReportStatusLoopCheck(reportJob.jobId).catch(() => {
        setReportJob({
          jobId: reportJob.jobId,
          downloadUrl: "",
          status: "failed",
        });
      });

      return;
    }

    if (reportJob.status === "done" && reportJob.downloadUrl) {
      triggerDownload(reportJob.downloadUrl);
      openSucessAlert?.(t("ottoTrialPeriod.report.successMessage"));
      onClose();
      return;
    }

    if (reportJob.status === "failed") {
      openErrorAlert?.();
    }
  }, [onClose, openErrorAlert, openSucessAlert, reportJob]);

  useEffect(() => {
    return () => {
      if (reportPollingTimeoutRef.current) {
        clearTimeout(reportPollingTimeoutRef.current);
      }
    };
  }, []);

  const generateReport: SubmitHandler<FormValues> = async (values) => {
    try {
      const params: Record<string, string> = {
        countryCode,
        startDateFrom: `${values.startDate}T00:00:00`,
        startDateTo: `${values.endDate}T23:59:59`,
      };

      if (
        values.campaignType &&
        values.campaignType !== ParticipantReportType.ALL
      ) {
        params.campaignType =
          values.campaignType === ParticipantReportType.BATCH
            ? ParticipantReportType.EMAIL
            : values.campaignType;
      }

      if (
        values.campaignStatus &&
        values.campaignStatus !== CampaignStatus.ALL
      ) {
        params.status = values.campaignStatus;
      }

      const response = await axios.get(
        `${CONFIG.API_URL}temporaryAccess/report`,
        {
          headers: {
            ...tokenHeaders.headers,
            countryCode,
          },
          params,
        },
      );

      setReportJob({
        jobId: response.data.jobId,
        downloadUrl: "",
        status: "processing",
      });
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message;
      openErrorAlert?.(errorMessage);
    }
  };

  const HorizontalLine = (
    <Box
      sx={{
        borderTop: "1px solid var(--gray-100)",
        marginBottom: 3,
        marginTop: 2,
      }}
    />
  );

  return (
    <MyModal
      title={t("ottoTrialPeriod.report.title")}
      modalChange={onClose}
      component="form"
      onSubmit={handleSubmit(generateReport)}
    >
      <Typography fontSize={16} fontWeight={400} marginBottom={4}>
        {t("ottoTrialPeriod.report.description")}
      </Typography>

      <Grid container spacing={2}>
        <Grid item md={3} xs={12}>
          <span>{t("ottoTrialPeriod.add.period")}</span>
        </Grid>
        <Grid item md={9} xs={12}>
          <Grid container spacing={2}>
            <Grid item md={6} xs={12}>
              <FormInputText
                id="startDate"
                type="date"
                label={t("ottoTrialPeriod.report.startDate")}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  inputProps: { max: "2100-01-01" },
                }}
                error={errors.startDate}
                {...register("startDate")}
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <FormInputText
                id="endDate"
                type="date"
                label={t("ottoTrialPeriod.report.endDate")}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  inputProps: { max: "2100-01-01" },
                }}
                error={errors.endDate}
                {...register("endDate")}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {HorizontalLine}

      <Grid container spacing={2}>
        <Grid item md={3} xs={12}>
          <span>{t("ottoTrialPeriod.report.campaignStatus")}</span>
        </Grid>

        <Grid item md={9} xs={12}>
          <Grid container spacing={2}>
            <Grid item md={6} xs={12}>
              <FormInputText
                select
                id="campaignStatus"
                label={t("ottoTrialPeriod.report.status")}
                error={errors.campaignStatus}
                value={selectedCampaignStatus || ""}
                {...register("campaignStatus")}
              >
                {Object.values(CampaignStatus).map((status) => (
                  <MenuItem key={status} value={status}>
                    {t(`ottoTrialPeriod.status.${status}`)}
                  </MenuItem>
                ))}
              </FormInputText>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {HorizontalLine}
      <Grid container spacing={2}>
        <Grid item md={3} xs={12}>
          <span>{t("ottoTrialPeriod.report.participants")}</span>
        </Grid>

        <Grid item md={9} xs={12}>
          <Grid container spacing={2}>
            <Grid item md={6} xs={12}>
              <FormInputText
                select
                id="campaignType"
                label={t("ottoTrialPeriod.report.participantType")}
                error={errors.campaignType}
                value={selectedCampaignType || ""}
                {...register("campaignType")}
              >
                {Object.values(ParticipantReportType).map((type) => (
                  <MenuItem key={type} value={type}>
                    {t(
                      `ottoTrialPeriod.add.participantTypeOptions.${type.toLowerCase()}`,
                    )}
                  </MenuItem>
                ))}
              </FormInputText>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {HorizontalLine}

      <p style={{ fontSize: 12, marginBottom: 30 }}>
        {t("ottoTrialPeriod.add.requiredFields")}
      </p>

      {reportJob.status === "processing" || formState.isSubmitting ? (
        <CircularProgress />
      ) : (
        <ActionButtons
          typeButton="submit"
          saveName={t("ottoTrialPeriod.report.downloadButton")}
          disableSave={!formState.isValid}
          enableLine={false}
        />
      )}
    </MyModal>
  );
}
